/**
 * Instance-default theme layer (Phase 2 theme cascade).
 *
 * EAA-1664: baked at build/deploy time via a committed `theme.instance.json`
 * at the app root — DevOps overwrites that file (or forks it per deployment)
 * before building a dedicated on-prem instance that only ever serves one
 * tenant's brand. This layer sits between the app preset and the tenant
 * override in `applyTheme()`'s cascade — most specific wins.
 *
 * For a dedicated single-tenant instance, this layer alone is meant to be
 * sufficient — pair it with `VITE_THEME_TENANT_FETCH_ENABLED=false` (read by
 * `useCustomTheme`) to skip the tenant-override fetch entirely.
 */
import type { CustomThemeConfig } from '@ai-content-hub/shared-styles';
import instanceThemeJson from '../../theme.instance.json';

export const instanceTheme: CustomThemeConfig = instanceThemeJson as CustomThemeConfig;

/**
 * Whether the tenant-override theme fetch (API call + Redux tenant lookup)
 * should run at all. Defaults to enabled; set to `false` for a dedicated
 * instance deployment that only ever shows the instance-default brand above.
 */
export const isTenantThemeFetchEnabled = (): boolean =>
    import.meta.env.VITE_THEME_TENANT_FETCH_ENABLED !== 'false';
